import { useState, useCallback } from "react";

/**
 * useChatFiles - Custom hook for managing chat files
 * @param {string} currentChatId - Current chat ID
 * @returns {Object} { chatFiles, getCurrentChatFiles, setCurrentChatFile, removeCurrentChatFile, handleFileRemove }
 */
export function useChatFiles(currentChatId = null) {
  const [chatFiles, setChatFiles] = useState({}); // { [chatId]: { mainQueryFiles: [], comparisonFiles: [], synthesisFiles: [] } }

  const normalizeFiles = (files) => ({
    // Backend/app code historically used both singular and plural keys.
    // Normalize them so the UI always reads from the plural categories.
    mainQueryFiles: files?.mainQueryFiles || files?.mainQueryFile || [],
    comparisonFiles: files?.comparisonFiles || files?.comparisonFile || [],
    synthesisFiles: files?.synthesisFiles || files?.synthesisFile || [],
    // Some parts of the app use debateFiles; keep it here for consistency.
    debateFiles: files?.debateFiles || files?.debateFile || [],
  });

  const getCurrentChatFiles = useCallback(() => {
    const chatId = currentChatId || "new"; // Use 'new' for new chats
    const filesForChat = chatFiles[chatId];
    const normalizedChat = filesForChat ? normalizeFiles(filesForChat) : null;

    const chatHasAnyFiles =
      (normalizedChat?.mainQueryFiles?.length || 0) > 0 ||
      (normalizedChat?.comparisonFiles?.length || 0) > 0 ||
      (normalizedChat?.synthesisFiles?.length || 0) > 0 ||
      (normalizedChat?.debateFiles?.length || 0) > 0;

    // During initial-query transitions, the app may briefly render a chat id
    // that doesn't yet have file state. If uploads exist in the 'new' bucket,
    // show them so the UI doesn't "blink" to an empty file list.
    const newFiles = chatFiles["new"];
    const normalizedNew = newFiles ? normalizeFiles(newFiles) : null;
    const newHasAnyFiles =
      (normalizedNew?.mainQueryFiles?.length || 0) > 0 ||
      (normalizedNew?.comparisonFiles?.length || 0) > 0 ||
      (normalizedNew?.synthesisFiles?.length || 0) > 0 ||
      (normalizedNew?.debateFiles?.length || 0) > 0;

    // If current chat has files, always prefer them.
    if (normalizedChat && chatHasAnyFiles) return normalizedChat;

    // During transitions (e.g. optimistic `temp_...` ids), the app may briefly have
    // an empty chat bucket. In that case, fall back to whatever is in `new`.
    if (chatId && chatId.startsWith("temp_")) {
      if (normalizedNew) return normalizedNew;
    }

    // For real chat ids, fall back only if `new` still has files.
    if (newHasAnyFiles) return normalizedNew;

    // Otherwise return what we have for the current chat, or empty lists.
    return normalizedChat || normalizeFiles(null);
  }, [chatFiles, currentChatId]);

  // Renamed to addChatFile for clarity, but supporting old usage might be tricky without breaking changes.
  // I will create a new function addChatFile and keep setCurrentChatFile as a wrapper that adds single file (clearing others? or appending?).
  // User asked for multiple files, so "setting" a single file usually implies replacing or adding.
  // Let's change setCurrentChatFile to addChatFile and update consumers.

  const addChatFile = useCallback(
    (fileCategory, fileData) => {
      // fileCategory: 'mainQueryFiles', 'comparisonFiles', 'synthesisFiles'
      // Note: Consumers pass 'mainQueryFile' (singular). I should map it or expect plural.

      // Mapping singular to plural for backward compat compatibility during refactor
      let category = fileCategory;
      if (fileCategory === "mainQueryFile") category = "mainQueryFiles";
      if (fileCategory === "comparisonFile") category = "comparisonFiles";
      if (fileCategory === "synthesisFile") category = "synthesisFiles";

      const chatId = currentChatId || "new";
      setChatFiles((prev) => {
        const currentChatFiles = prev[chatId] || {
          mainQueryFiles: [],
          comparisonFiles: [],
          synthesisFiles: [],
        };
        const currentList = currentChatFiles[category] || [];

        // Avoid duplicates by fileId, s3Key or name
        const exists = currentList.some(
          (f) =>
            (f.fileId && fileData.fileId && f.fileId === fileData.fileId) ||
            f.s3Key === fileData.s3Key ||
            (f.fileName === fileData.fileName &&
              f.fileSize === fileData.fileSize),
        );
        if (exists) return prev;

        return {
          ...prev,
          [chatId]: {
            ...currentChatFiles,
            [category]: [...currentList, fileData],
          },
        };
      });
    },
    [currentChatId],
  );

  // Helper to set files (replace all) - e.g. when loading chat.
  // Optional third arg: targetChatId — use when loading a chat so files are set for that chat, not the (possibly stale) currentChatId.
  const setChatFilesList = useCallback(
    (fileCategory, filesList, targetChatId) => {
      let category = fileCategory;
      if (fileCategory === "mainQueryFile") category = "mainQueryFiles";
      if (fileCategory === "comparisonFile") category = "comparisonFiles";
      if (fileCategory === "synthesisFile") category = "synthesisFiles";

      const chatId =
        targetChatId !== undefined && targetChatId !== null
          ? targetChatId
          : currentChatId || "new";
      setChatFiles((prev) => ({
        ...prev,
        [chatId]: {
          ...(prev[chatId] || {
            mainQueryFiles: [],
            comparisonFiles: [],
            synthesisFiles: [],
            debateFiles: [],
          }),
          [category]: filesList,
        },
      }));
    },
    [currentChatId],
  );

  const removeChatFile = useCallback(
    (fileCategory, fileIdentifier) => {
      // fileIdentifier can be s3Key or index or just remove all if not specified?
      // Let's assume we remove by s3Key or fileName.

      let category = fileCategory;
      if (fileCategory === "mainQueryFile") category = "mainQueryFiles";
      if (fileCategory === "comparisonFile") category = "comparisonFiles";
      if (fileCategory === "synthesisFile") category = "synthesisFiles";

      const chatId = currentChatId || "new";
      setChatFiles((prev) => {
        const currentChatFiles = prev[chatId] || {};
        const currentList = currentChatFiles[category] || [];

        // If fileIdentifier is null, maybe clear all? Or do nothing.
        // Existing removeCurrentChatFile removed the *single* file.
        // If we want to remove a specific file, we need an ID.
        // If we want to clear the category, we can do that too.

        let newList;
        if (!fileIdentifier) {
          // Clear all if no identifier
          newList = [];
        } else {
          newList = currentList.filter(
            (f) =>
              f.fileId !== fileIdentifier &&
              f.s3Key !== fileIdentifier &&
              f.fileName !== fileIdentifier,
          );
        }

        return {
          ...prev,
          [chatId]: {
            ...currentChatFiles,
            [category]: newList,
          },
        };
      });
    },
    [currentChatId],
  );

  const handleFileRemove = useCallback(
    (fileCategory, fileToDelete) => {
      // fileToDelete is the file object or s3Key
      const currentFiles = getCurrentChatFiles();

      let category = fileCategory;
      if (fileCategory === "mainQueryFile") category = "mainQueryFiles";
      if (fileCategory === "comparisonFile") category = "comparisonFiles";
      if (fileCategory === "synthesisFile") category = "synthesisFiles";

      const list = currentFiles[category] || [];
      const file = fileToDelete
        ? list.find(
            (f) =>
              f === fileToDelete ||
              f.fileId === fileToDelete ||
              f.s3Key === fileToDelete ||
              f.fileName === fileToDelete,
          )
        : null;

      if (file?.fileUrl && !file?.s3Url) {
        URL.revokeObjectURL(file.fileUrl);
      }

      removeChatFile(
        category,
        fileToDelete
          ? fileToDelete.fileId ||
              fileToDelete.s3Key ||
              fileToDelete.fileName ||
              fileToDelete
          : null,
      );
    },
    [getCurrentChatFiles, removeChatFile],
  );

  return {
    chatFiles,
    setChatFiles,
    getCurrentChatFiles,
    addChatFile,
    setChatFilesList,
    removeChatFile,
    handleFileRemove,
    // Aliases for compatibility (though we should update consumers)
    setCurrentChatFile: addChatFile,
    removeCurrentChatFile: removeChatFile,
  };
}
